import { createTheme } from "@mui/material/styles";

const prop = {
  primary: "#5b648f",
  primaryXS: "#5b648f12",
  dark: "#414141",
  success: "#00E396",
  error: "#ff0000",
  warning: "#FFCC00",
  info: "#00e1ff",
  white: "#fff",
  whitemd: "#D1D5DB",
  black: "#000",
  gray: "#7a7a7a",
  graySM: "#80808024",
};

// Theme options (exported for ThemeLocaleProvider to recreate with direction)
export const themeOptions = {
  status: {
    danger: prop.danger,
  },
  palette: {
    default: {
      contrastText: prop.black,
    },
    primary: {
      main: prop.primary,
      contrastText: prop.white,
    },
    primaryXS: {
      main: prop.primaryXS,
      contrastText: prop.primary,
    },
    secondary: {
      main: prop.dark,
      contrastText: prop.white,
    },
    white: {
      main: prop.white,
      contrastText: prop.gray,
    },
    whitemd: {
      main: prop.whitemd,
      contrastText: prop.gray,
    },
    error: {
      main: prop.error,
      contrastText: prop.white,
    },
    warning: {
      main: prop.warning,
      contrastText: prop.white,
    },
    info: {
      main: prop.info,
      contrastText: prop.white,
    },
    success: {
      main: prop.success,
      contrastText: prop.white,
    },
    gray: {
      main: prop.gray,
      contrastText: prop.white,
    },
    grayXS: {
      main: prop.graySM,
      contrastText: prop.gray,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "var(--card-radius)",
          boxShadow: "var(--shadow-xs)",
          backgroundColor: "var(--white-color)",
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "var(--white-color)",
          color: "var(--primary-color)",
          backdropFilter: "blur(15px)",
          fontSize: "12px",
          textTransform: "uppercase",
        },
        arrow: {
          color: "var(--white-color)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          transition: "0.2s",
          "&:hover": {
            color: "var(--primaryDark-color)",
            ".MuiListItemIcon-root": {
              color: "var(--primary-color)",
            },
          },
          "&.Mui-selected": {
            color: "var(--primaryDark-color)",
            backgroundColor: "var(--grayXS-color)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "var(--input-radius)",
          backgroundColor: "var(--white-color)",
          fieldset: {
            border: "1px solid var(--graySM-color)",
          },
          "&:hover fieldset": {
            borderColor: `${prop.primary} !important`,
          },
          ":disabled": {
            backgroundColor: "var(--grayXS-color) !important",
            color: "var(--dark-color)",
          },
          "&.Mui-disabled": {
            backgroundColor: "var(--grayXS-color) !important",
            color: "var(--dark-color)",
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#c9cacf",
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        label: {
          padding: "0 10px",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "var(--btn-radius)",
          letterSpacing: "1px",
          fontWeight: "400",
          textTransform: "none",
          ":disabled": {
            backgroundColor: prop.graySM,
            color: prop.gray,
          },
        },
        endIcon: {
          marginLeft: "3px",
        },
      },
      variants: [
        {
          props: { variant: "text" },
          style: {
            paddingLeft: "10px",
            paddingRight: "10px",
            backgroundColor: "var(--primarySM-color)",
            color: "var(--primary-color)",
            "&:hover": {
              backgroundColor: "var(--primaryMD-color)",
            },
          },
        },
      ],
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          "&, &:hover, &.MuiFilledInput-root.Mui-focused": {
            backgroundColor: "var(--grayXS-color)",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
          },
          "&.Mui-disabled": {
            backgroundColor: "var(--primaryXS-color)",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          flex: "1 1 auto",
          flexShrink: 0,
          // justifyContent: "start",
          width: "fit-content",

          maxWidth: "unset !important",
          minWidth: "unset !important",
          minHeight: "unset !important",
          fontSize: "14px",
          fontWeight: "400",
          textTransform: "none",
          lineHeight: "inherit",
          padding: "5px 12px",
          letterSpacing: "0.5px",
          borderRadius: "10px",
          color: `black !important`,
          opacity: "1 !important",
          "& .material-icons, .material-icons-round": {
            marginBottom: "0 !important",
            marginRight: "4px",
          },

          "& svg": {
            marginBottom: "0 !important",
            marginRight: "4px",
          },
        },

        labelIcon: {
          marginRight: "4px",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          position: "relative",
          backgroundColor: prop.primaryXS,
          borderRadius: "12px",
          minHeight: "unset",
          padding: "4px",
          boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 2px 0px",
        },

        flexContainer: {
          height: "100%",
          position: "relative",
          zIndex: 10,
        },

        fixed: {
          overflow: "unset !important",
          overflowX: "unset !important",
        },

        vertical: {
          "& .MuiTabs-indicator": {
            width: "100%",
          },
        },

        indicator: {
          height: "100%",
          border: "1px solid #e5e5e5",
          borderRadius: "11px",
          backgroundColor: "white",
          transition: "all 300ms ease",
        },
      },
    },
  },
};

const theme = createTheme(themeOptions);

export default theme;
